import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import {
    Box,
    Container,
    Typography,
    Button,
    Card,
    CardContent,
    Radio,
    RadioGroup,
    FormControlLabel,
    FormControl,
    Chip,
    IconButton,
    LinearProgress,
    Fade,
    useTheme,
    useMediaQuery,
    Stack,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SpeedIcon from '@mui/icons-material/Speed';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import PsychologyIcon from '@mui/icons-material/Psychology';

const Onboarding = ({ onSkip, onComplete }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const navigate = useNavigate();
    const { user, refreshUserData } = useAuth();

    const [step, setStep] = useState(0);
    const [plan, setPlan] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedGoal, setSelectedGoal] = useState(null);
    const [saving, setSaving] = useState(false);

    const fetchPlan = useCallback(async () => {
        try {
            const res = await api.get('/activation/plan');
            setPlan(res.data);
            setSelectedGoal(res.data.primaryGoal || 'speed_comprehension');

            if (res.data.shouldShowOnboarding) {
                await api.post('/activation/presented').catch(() => {});
            } else {
                onComplete();
            }
        } catch (err) {
            console.error('Failed to fetch activation plan:', err);
            onComplete();
        } finally {
            setLoading(false);
        }
    }, [onComplete]);

    useEffect(() => {
        fetchPlan();
    }, [fetchPlan]);

    const handleSkip = async () => {
        try {
            await api.post('/activation/skip');
        } catch (_) {}
        onSkip();
    };

    const handleGoalSelect = async () => {
        setSaving(true);
        try {
            await api.post('/activation/goal', { goal: selectedGoal });
            await refreshUserData();
        } catch (_) {}
        setSaving(false);
        setStep(2);
    };

    const handleStartSession = async () => {
        try {
            await api.post('/activation/session-started');
            await api.post('/activation/complete');
            await refreshUserData();
        } catch (_) {}
        onComplete();
        if (plan?.recommendedRoute) {
            navigate(plan.recommendedRoute);
        } else {
            navigate('/reader');
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <LinearProgress sx={{ width: 200 }} />
            </Box>
        );
    }

    if (!plan) {
        onComplete();
        return null;
    }

    const completedCount = plan.completedItems?.length || 0;
    const totalCount = plan.checklistItems?.length || 5;
    const progress = (completedCount / totalCount) * 100;

    const acknowledgmentText = () => {
        const ctx = plan.context;
        if (ctx.hasChallenge) return 'You joined through a reading challenge. Let\'s pick up where you left off.';
        if (ctx.hasBaseline && ctx.hasBlueprint) return 'Your reading assessment and blueprint are ready. Let\'s put them to work.';
        if (ctx.hasBaseline) return 'You\'ve already completed your reading assessment. Your baseline is set.';
        if (ctx.hasBlueprint) return 'Your Reading Blueprint is ready. Let\'s start training.';
        if (ctx.isTrial) return 'Your free trial is active. Let\'s make the most of it.';
        if (ctx.isPaid && !ctx.isTrial) return 'Your membership is active. Let\'s get you reading.';
        return 'Welcome to ReaderFLEX. Let\'s get you started.';
    };

    return (
        <Container maxWidth="md" sx={{ py: { xs: 3, md: 6 } }}>
            {/* Skip button */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                <IconButton onClick={handleSkip} size="small" aria-label="Skip onboarding">
                    <CloseIcon />
                </IconButton>
            </Box>

            {/* Progress indicator */}
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 4 }}>
                {[0, 1, 2, 3].map(i => (
                    <Box
                        key={i}
                        sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            backgroundColor: i <= step ? theme.palette.primary.main : theme.palette.divider,
                            transition: 'background-color 0.3s',
                        }}
                    />
                ))}
            </Box>

            {/* Step 0: Acknowledge */}
            {step === 0 && (
                <Fade in={true}>
                    <Card sx={{ p: { xs: 3, md: 5 }, textAlign: 'center' }}>
                        <CardContent>
                            <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>
                                Welcome{user?.profile?.displayName ? `, ${user.profile.displayName}` : ''}.
                            </Typography>
                            <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 480, mx: 'auto' }}>
                                {acknowledgmentText()}
                            </Typography>

                            {/* Context chips */}
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center', mb: 4 }}>
                                {plan.context.hasBaseline && (
                                    <Chip icon={<SpeedIcon />} label="Assessment Complete" color="primary" variant="outlined" />
                                )}
                                {plan.context.hasBlueprint && (
                                    <Chip icon={<MenuBookIcon />} label="Blueprint Ready" color="primary" variant="outlined" />
                                )}
                                {plan.context.hasChallenge && (
                                    <Chip icon={<EmojiEventsIcon />} label="Challenge Pending" color="primary" variant="outlined" />
                                )}
                                {plan.context.isTrial && (
                                    <Chip label="Trial Active" color="secondary" />
                                )}
                                {plan.context.isPaid && !plan.context.isTrial && (
                                    <Chip label="Pro Member" color="secondary" />
                                )}
                            </Box>

                            <Button
                                variant="contained"
                                size="large"
                                endIcon={<ArrowForwardIcon />}
                                onClick={() => setStep(1)}
                                sx={{ py: 1.5, px: 5 }}
                            >
                                Continue
                            </Button>
                        </CardContent>
                    </Card>
                </Fade>
            )}

            {/* Step 1: Goal Selection */}
            {step === 1 && (
                <Fade in={true}>
                    <Card sx={{ p: { xs: 3, md: 5 } }}>
                        <CardContent>
                            <Typography variant="h5" sx={{ fontWeight: 700, mb: 1, textAlign: 'center' }}>
                                What's your primary reading goal?
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 4, textAlign: 'center' }}>
                                We'll tailor your training to match.
                            </Typography>

                            <FormControl component="fieldset" sx={{ width: '100%' }}>
                                <RadioGroup value={selectedGoal} onChange={e => setSelectedGoal(e.target.value)}>
                                    <Stack spacing={1}>
                                        {[
                                            { id: 'speed_comprehension', label: 'Read faster while protecting comprehension', icon: <SpeedIcon /> },
                                            { id: 'stop_rereading', label: 'Stop rereading', icon: <PsychologyIcon /> },
                                            { id: 'focus', label: 'Improve focus', icon: <PsychologyIcon /> },
                                            { id: 'retention', label: 'Remember more', icon: <MenuBookIcon /> },
                                            { id: 'finish_books', label: 'Finish more books', icon: <MenuBookIcon /> },
                                            { id: 'habit', label: 'Build a consistent reading habit', icon: <EmojiEventsIcon /> },
                                            { id: 'professional', label: 'Improve professional or academic reading', icon: <MenuBookIcon /> },
                                        ].map(goal => (
                                            <FormControlLabel
                                                key={goal.id}
                                                value={goal.id}
                                                control={<Radio />}
                                                label={
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        {goal.icon}
                                                        <Typography>{goal.label}</Typography>
                                                    </Box>
                                                }
                                                sx={{
                                                    border: 1,
                                                    borderColor: selectedGoal === goal.id ? 'primary.main' : 'divider',
                                                    borderRadius: 2,
                                                    p: 1.5,
                                                    m: 0,
                                                    cursor: 'pointer',
                                                    transition: 'border-color 0.2s',
                                                    '&:hover': { borderColor: 'primary.light' },
                                                }}
                                            />
                                        ))}
                                    </Stack>
                                </RadioGroup>
                            </FormControl>

                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
                                <Button onClick={() => setStep(0)}>Back</Button>
                                <Button
                                    variant="contained"
                                    size="large"
                                    onClick={handleGoalSelect}
                                    disabled={saving || !selectedGoal}
                                    endIcon={<ArrowForwardIcon />}
                                >
                                    {saving ? 'Saving...' : 'Continue'}
                                </Button>
                            </Box>
                        </CardContent>
                    </Card>
                </Fade>
            )}

            {/* Step 2: Start Session */}
            {step === 2 && (
                <Fade in={true}>
                    <Card sx={{ p: { xs: 3, md: 5 }, textAlign: 'center' }}>
                        <CardContent>
                            <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
                                Ready to begin?
                            </Typography>
                            <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 480, mx: 'auto' }}>
                                {plan.context.hasChallenge
                                    ? 'Your challenge is waiting. Read the passage and answer the questions to complete it.'
                                    : plan.context.hasBaseline
                                        ? 'Your baseline is set. Let\'s start your first guided reading session.'
                                        : 'Let\'s calibrate your reading speed first, then jump into training.'}
                            </Typography>

                            <Button
                                variant="contained"
                                size="large"
                                onClick={handleStartSession}
                                sx={{ py: 1.5, px: 6, fontSize: '1.1rem' }}
                            >
                                {plan.recommendedAction || 'Start Reading'}
                            </Button>

                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                                Takes about 5 minutes.
                            </Typography>
                        </CardContent>
                    </Card>
                </Fade>
            )}

            {/* Step 3: Checklist (shown after session if user returns) */}
            {step === 3 && (
                <Fade in={true}>
                    <Card sx={{ p: { xs: 3, md: 5 } }}>
                        <CardContent>
                            <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, textAlign: 'center' }}>
                                Your Progress
                            </Typography>

                            <LinearProgress variant="determinate" value={progress} sx={{ height: 8, borderRadius: 4, mb: 3 }} />

                            <Stack spacing={1.5}>
                                {plan.checklistItems?.map(item => (
                                    <Box
                                        key={item.id}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1.5,
                                            p: 1.5,
                                            borderRadius: 2,
                                            backgroundColor: item.completed ? 'success.main' : 'background.default',
                                            opacity: item.completed ? 0.9 : 1,
                                        }}
                                    >
                                        <CheckCircleIcon
                                            color={item.completed ? 'success' : 'disabled'}
                                            sx={{ fontSize: 24 }}
                                        />
                                        <Typography
                                            variant="body1"
                                            color={item.completed ? 'success.contrastText' : 'text.primary'}
                                            sx={{ textDecoration: item.completed ? 'line-through' : 'none' }}
                                        >
                                            {item.label}
                                        </Typography>
                                    </Box>
                                ))}
                            </Stack>

                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                                <Button variant="contained" size="large" onClick={handleStartSession}>
                                    {plan.remainingItems?.length === 0 ? 'Go to Dashboard' : plan.recommendedAction || 'Continue'}
                                </Button>
                            </Box>
                        </CardContent>
                    </Card>
                </Fade>
            )}
        </Container>
    );
};

export default Onboarding;
